import { Component, input, OnInit, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { UpdateBeaconDto } from '../../dto/update-beacon.dto';
import { BeaconGroupService } from '../../../../spook/beacon-group/service/beacon-group.service';
import { BeaconGroupDto } from '../../../../spook/beacon-group/model/beacon-group.dto';
import { BeaconProjectionDto } from '../../dto/beacon-projection.dto';
import { BeaconService } from '../../service/beacon.service';
import { UsersService } from '../../../../shared/services/users/users.service';
import { Roles, SecurityExpressions } from '../../../../shared/utils/security-expressions';

function vinValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null;
    }
    const vinPattern = /^[A-HJ-NPR-Z0-9]{17}$/;
    return vinPattern.test(value.toUpperCase()) ? null : { invalidVin: true };
  };
}

@Component({
  selector: 'app-beacon-form',
  imports: [ReactiveFormsModule],
  templateUrl: './beacon-form.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './beacon-form.css',
})
export class BeaconForm implements OnInit {
  id = input.required<string>();
  beacon = input.required<BeaconProjectionDto>();

  formClose = output<void>();

  groupOptions = signal<{ label: string; value: string }[]>([]);
  isLoading = signal(false);
  isSaving = signal(false);
  hasError = signal(false);
  loadError = signal<string | null>(null);
  serverError = signal<string | null>(null);

  userGroups = signal<Roles[]>([]);

  form: FormGroup;

  constructor(
    private _fb: FormBuilder,
    private _beaconGroupService: BeaconGroupService,
    private _beaconService: BeaconService,
    private _userService: UsersService,
  ) {
    this.form = this._fb.group({
      beaconGroupId: [null, Validators.required],
      holderName: ['', Validators.required],
      assetName: ['', Validators.required],
      vin: ['', [Validators.required, vinValidator()]],
      trackingEnabled: [false],
    });
  }

  ngOnInit(): void {
    this._loadGroupOptions();

    this.form.setValue({
      beaconGroupId: this.beacon().beaconGroupId,
      holderName: this.beacon().holderName,
      assetName: this.beacon().assetName,
      vin: this.beacon().vin,
      trackingEnabled: this.beacon().trackingEnabled
    });

    const user = this._userService.getUser();
    const userGroups = user?.['cognito:groups'] as Roles[];

    this.userGroups.set(userGroups || []);
  }

  private _loadGroupOptions(): void {
    this.isLoading.set(true);
    this.hasError.set(false);
    this.loadError.set(null);

    this._beaconGroupService.getBeaconGroups({ page: 0, pageSize: 100 }).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const groups = response.data.map((group: BeaconGroupDto) => ({
            label: group.name,
            value: group.id,
          }));
          this.groupOptions.set(groups);
        }
        this.isLoading.set(false);
      },
      error: (err) => {
        this.hasError.set(true);
        this.loadError.set(err?.error?.message || err?.message || 'Error al cargar los grupos');
        this.isLoading.set(false);
      },
    });
  }

  fieldHasError(field: string): boolean {
    const control = this.form.get(field);
    return !!(control && control.invalid && control.touched);
  }

  fieldError(field: string): string {
    const control = this.form.get(field);
    if (!control || !control.errors || !control.touched) {
      return '';
    }
    if (control.errors['required']) {
      return 'Este campo es obligatorio';
    }
    if (control.errors['invalidVin']) {
      return 'VIN inválido — debe tener 17 caracteres alfanuméricos (excluye I, O, Q)';
    }
    return '';
  }

  onSubmit(): void {
    this.serverError.set(null);
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);
    const beaconValues: UpdateBeaconDto = this.form.value;

    this._beaconService.updateBeacon(this.id(), beaconValues).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.formClose.emit();
      },
      error: (err) => {
        this.isSaving.set(false);
        this.serverError.set(err?.error?.message || err?.message || 'Error al actualizar el beacon');
      },
    });
  }

  onCancel(): void {
    this.formClose.emit();
  }

  userCanEdit() {
    const requiredRoles = SecurityExpressions.ONLY_ADMINS;
    const userRoles = this.userGroups();

    return userRoles.some(ur => requiredRoles.includes(ur));
  }
}
